import { followLink, ctrlClickMac, ctrlClickWindows } from './ReactEditor.locstring.json';
import loc, { format } from 'owa-localize';
import { observer } from 'mobx-react-lite';
import EditorViewState from '../store/schema/EditorViewState';
import getDefaultComposeFormat from '../utils/getDefaultComposeFormat';
import { addContentOperator, removeContentOperator } from '../utils/ContentOperator';
import createPluginUtils from 'owa-editor-plugin-utils/lib/utils/createPluginUtils';
import isEditorPluginEx from 'owa-editor-plugin-utils/lib/utils/isEditorPluginEx';
import { transformElementForDarkMode } from 'owa-dark-mode-utilities';
import { isFeatureEnabled } from 'owa-feature-flags';
import ViewStateManagePlugin from '../internalPlugin/ViewStateManagePlugin';
import SelectImagePlugin from '../internalPlugin/SelectImagePlugin';
import createSnapshotsService from '../utils/createSnapshotsService';
import { isMac } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import { Editor } from 'roosterjs-editor-core';
import {
    DefaultFormat,
    EditorOptions,
    EditorPlugin,
    ExperimentalFeatures,
    IEditor,
} from 'roosterjs-editor-types';
import { HtmlSanitizer, findClosestElementAncestor } from 'roosterjs-editor-dom';
import PluginUI from './PluginUI';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import updateContent from '../actions/updateContent';
import {
    ContentEdit,
    ContextMenu,
    CustomReplace,
    CutPasteListChain,
    HyperLink,
    ImageResize,
    Paste,
    TableResize,
    Watermark,
} from 'owa-editor-lazy-internal-plugins';
import CtsWorkaroundPlugin from '../internalPlugin/CtsWorkaroundPlugin';
import { getPaletteAsRawColors } from 'owa-theme';
import getTriggerEventAPIWithMeasure from '../utils/getTriggerEventAPIWithMeasure';

export interface ReactEditorProps {
    viewState: EditorViewState;
    plugins?: EditorPlugin[];
    classNames?: string;
    updateOnKeyPress?: boolean;
    updateOnDispose?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    watermark?: string;
    watermarkFormat?: DefaultFormat;
    ariaLabel?: string;
    isDarkMode?: boolean;
    scrollContainerSelector?: string;
    focusOnInit?: boolean;
    measurePerf?: boolean;
}

const styles = require('./ReactEditor.scss');
const classname = require('classnames/bind').bind(styles);
const MIN_IMAGE_SIZE = 10;

export interface ReactEditorHandle {
    focus: () => void;
}

const ReactEditor = observer(
    function ReactEditor(props: ReactEditorProps, ref: React.Ref<ReactEditorHandle>) {
        const {
            onFocus,
            classNames,
            viewState,
            watermark,
            ariaLabel,
            isDarkMode,
            plugins,
            focusOnInit,
            watermarkFormat,
            scrollContainerSelector,
            onBlur,
            updateOnKeyPress,
            updateOnDispose,
            measurePerf,
        } = props;

        let editor = React.useRef<IEditor>();
        let editorDiv = React.useRef<HTMLDivElement>();
        let wasInDarkMode = React.useRef<boolean>(isDarkMode);

        React.useImperativeHandle(ref, () => ({
            focus: () => {
                editor.current?.focus();
            },
        }));

        React.useEffect(() => {
            editor.current = new Editor(editorDiv.current, getEditorOptions());
            updateContent(viewState, editor.current.getContent(), true /*isInitializing*/);
            wasInDarkMode.current = isDarkMode;

            if (focusOnInit) {
                editor.current.focus();
            }

            addContentOperator(
                viewState,
                editor.current /* htmlEditor */,
                null /* plainTextEditor */
            );

            return () => {
                if (editor.current) {
                    removeContentOperator(viewState, editor.current, null /*plainTextEditor*/);
                    editor.current.dispose();
                    editor.current = null;
                }
            };
        }, [viewState.editorId]);

        const getEditorOptions = React.useCallback(() => {
            const defaultFormat = getDefaultComposeFormat(isDarkMode);
            const clickableLinkTooltip = format(
                loc(followLink),
                isMac() ? loc(ctrlClickMac) : loc(ctrlClickWindows)
            );

            const allPlugins = [
                ...(plugins || []),
                new ContentEdit({
                    mergeInNewLineWhenBackspaceOnFirstChar: true,
                }),
                new HyperLink(href => clickableLinkTooltip + '\r\n' + href),
                new Paste(),
                new TableResize(),
                new CustomReplace(),
                new CutPasteListChain(),
                watermark && new Watermark(watermark, watermarkFormat),
                new SelectImagePlugin(viewState),
                new ImageResize(
                    MIN_IMAGE_SIZE,
                    MIN_IMAGE_SIZE,
                    getPaletteAsRawColors().themePrimary,
                    true /*forcePreserveRatio*/
                ),
                new ViewStateManagePlugin(viewState, updateOnKeyPress, updateOnDispose),
                new ContextMenu(),
                CtsWorkaroundPlugin,
            ];

            const pluginUtils = createPluginUtils(editorDiv.current);
            allPlugins.forEach(
                plugin => isEditorPluginEx(plugin) && plugin.setPluginUtils(pluginUtils)
            );

            const initialContent = HtmlSanitizer.convertInlineCss(viewState.content);
            const experimentalFeatures: ExperimentalFeatures[] = [
                ExperimentalFeatures.ListChain,
                ExperimentalFeatures.MergePastedLine,
                ExperimentalFeatures.NewBullet,
                ExperimentalFeatures.NewIndentation,
                ExperimentalFeatures.NewNumbering,
            ];

            if (isFeatureEnabled('doc-edge-link')) {
                experimentalFeatures.push(ExperimentalFeatures.PasteWithLinkPreview);
            }

            if (isFeatureEnabled('cmp-singleDirImgResize')) {
                experimentalFeatures.push(ExperimentalFeatures.SingleDirectionResize);
            }

            const coreApiOverride = measurePerf
                ? {
                      triggerEvent: getTriggerEventAPIWithMeasure(viewState.editorId),
                  }
                : null;

            const options: EditorOptions = {
                plugins: allPlugins,
                defaultFormat: defaultFormat,
                undoSnapshotService: createSnapshotsService(viewState),
                initialContent: initialContent,
                inDarkMode: isDarkMode,
                experimentalFeatures,
                allowKeyboardEventPropagation: true,
                scrollContainer:
                    scrollContainerSelector &&
                    findClosestElementAncestor(
                        editorDiv.current,
                        editorDiv.current.ownerDocument.body,
                        scrollContainerSelector
                    ),
                onExternalContentTransform: element =>
                    transformElementForDarkMode(
                        element,
                        getPaletteAsRawColors().white,
                        true /* useSimpleMethod */
                    ),
                coreApiOverride,
            };

            return options;
        }, [viewState.editorId]);

        const onEditorBlur = React.useCallback(() => {
            updateContent(viewState, editor.current.getContent());
            if (onBlur) {
                onBlur();
            }
        }, [viewState.editorId]);

        const isRtl = viewState.textDirection
            ? viewState.textDirection === 'rtl'
            : getUserConfiguration().SessionSettings.IsUserCultureRightToLeft;

        if (editor.current && isDarkMode != wasInDarkMode.current) {
            wasInDarkMode.current = isDarkMode;
            editor.current.setDarkModeState(isDarkMode);
        }

        return (
            <>
                <div
                    ref={editorDiv}
                    dir={isRtl ? 'rtl' : 'ltr'}
                    className={classname(classNames, styles.overflowHidden, styles.textSelectable, {
                        resetColors: !isDarkMode,
                        anchorReset: isDarkMode,
                    })}
                    onBlur={onEditorBlur}
                    onFocus={onFocus}
                    role="textbox"
                    aria-multiline="true"
                    aria-label={ariaLabel ? ariaLabel : watermark}
                />
                {/**
                 * Add an empty DIV to workaround https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/8885
                 */}
                <div />
                <PluginUI plugins={plugins} />
            </>
        );
    },
    { forwardRef: true }
);

export default ReactEditor;
